import {
  CHAT_DATE_GROUP_LABEL_KEYS,
  CHAT_DATE_GROUP_MONTH_DAYS,
  CHAT_DATE_GROUP_WEEK_DAYS,
} from "../constants/chat-date-group";
import type { IChatListObject } from "../types/chat";
import type {
  IAddDaysProps,
  IChatDateGroup,
  IChatDateGroupIdentity,
  IFormatChatDateGroupLabelProps,
  IGroupChatsByDateProps,
  IResolveChatDateGroupProps,
} from "../types/chat-date-group";
import { EChatDateGroupKind } from "../types/chat-date-group";

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = ({ date, days }: IAddDaysProps): Date => {
  const shifted = new Date(date);
  shifted.setDate(shifted.getDate() + days);
  return shifted;
};

const padMonth = (monthIndex: number): string =>
  String(monthIndex + 1).padStart(2, "0");

// NOTE: Resolves the activity timestamp the list sorts and groups by. Servers
// older than NSAI-4559 do not send lastMessageAt, so createdAt stays as the
// fallback rather than dropping the chat out of the list entirely.
export const getChatActivityDate = (chat: IChatListObject): Date => {
  if (chat.lastMessageAt) {
    const lastMessageDate = new Date(chat.lastMessageAt);

    if (!Number.isNaN(lastMessageDate.getTime())) {
      return lastMessageDate;
    }
  }

  return new Date(chat.createdAt);
};

// NOTE: All boundaries are local midnights rather than rolling "now minus N
// hours" offsets — a chat from 23:00 yesterday belongs under Yesterday, not
// under Today, however late the user opens the app.
export const resolveChatDateGroup = ({
  date,
  now,
}: IResolveChatDateGroupProps): IChatDateGroupIdentity => {
  const todayStart = startOfDay(now);
  const yesterdayStart = addDays({ date: todayStart, days: -1 });
  const weekStart = addDays({
    date: todayStart,
    days: -CHAT_DATE_GROUP_WEEK_DAYS,
  });
  const monthStart = addDays({
    date: todayStart,
    days: -CHAT_DATE_GROUP_MONTH_DAYS,
  });

  if (date >= todayStart) {
    return { key: EChatDateGroupKind.TODAY, kind: EChatDateGroupKind.TODAY };
  }

  if (date >= yesterdayStart) {
    return {
      key: EChatDateGroupKind.YESTERDAY,
      kind: EChatDateGroupKind.YESTERDAY,
    };
  }

  if (date >= weekStart) {
    return {
      key: EChatDateGroupKind.PREVIOUS_7_DAYS,
      kind: EChatDateGroupKind.PREVIOUS_7_DAYS,
    };
  }

  if (date >= monthStart) {
    return {
      key: EChatDateGroupKind.PREVIOUS_30_DAYS,
      kind: EChatDateGroupKind.PREVIOUS_30_DAYS,
    };
  }

  // NOTE: Within the current year fall back to month names ("May"); anything
  // older collapses into a single bucket per year.
  if (date.getFullYear() === now.getFullYear()) {
    return {
      key: `month-${date.getFullYear()}-${padMonth(date.getMonth())}`,
      kind: EChatDateGroupKind.MONTH,
    };
  }

  return {
    key: `year-${date.getFullYear()}`,
    kind: EChatDateGroupKind.YEAR,
  };
};

// NOTE: Buckets chats into date groups ordered newest-first, with the chats
// inside each bucket sorted by activity descending. Empty buckets are never
// emitted, so callers can render the result as-is.
export const groupChatsByDate = ({
  chats,
  now = new Date(),
}: IGroupChatsByDateProps): IChatDateGroup[] => {
  const groupsByKey = new Map<string, IChatDateGroup>();

  chats.forEach((chat) => {
    const activityDate = getChatActivityDate(chat);

    if (Number.isNaN(activityDate.getTime())) return;

    const { key, kind } = resolveChatDateGroup({ date: activityDate, now });
    const existingGroup = groupsByKey.get(key);

    if (!existingGroup) {
      groupsByKey.set(key, {
        key,
        kind,
        referenceDate: activityDate,
        chats: [chat],
      });
      return;
    }

    existingGroup.chats.push(chat);

    if (activityDate > existingGroup.referenceDate) {
      existingGroup.referenceDate = activityDate;
    }
  });

  const groups = Array.from(groupsByKey.values());

  groups.forEach((group) => {
    group.chats.sort(
      (chatA, chatB) =>
        getChatActivityDate(chatB).getTime() -
        getChatActivityDate(chatA).getTime(),
    );
  });

  return groups.sort(
    (groupA, groupB) =>
      groupB.referenceDate.getTime() - groupA.referenceDate.getTime(),
  );
};

// NOTE: Fixed buckets read their label from i18n; MONTH and YEAR are formatted
// from the bucket's own date so no translation key is needed per month.
export const formatChatDateGroupLabel = ({
  group,
  locale,
  translate,
}: IFormatChatDateGroupLabelProps): string => {
  if (group.kind === EChatDateGroupKind.MONTH) {
    return new Intl.DateTimeFormat(locale, { month: "long" }).format(
      group.referenceDate,
    );
  }

  if (group.kind === EChatDateGroupKind.YEAR) {
    return new Intl.DateTimeFormat(locale, { year: "numeric" }).format(
      group.referenceDate,
    );
  }

  return translate(CHAT_DATE_GROUP_LABEL_KEYS[group.kind]);
};
